import React, { useState, useRef } from "react";
import { Download, Copy, Check } from "lucide-react";
import InlineSpinner from "../shared/Spinner/InlineSpinner";
import CustomTooltip from "../Tooltip/CustomTooltip";
import logger from '../../services/Logger';

const TableWithCopy = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const tableRef = useRef(null);

  const extractTableData = () => {
    if (!tableRef.current) return { tsv: "", markdown: "", html: "" };

    const table = tableRef.current;
    const rows = table.querySelectorAll("tr");
    let tsvText = "";
    let markdownText = "";

    // Enhanced HTML for better document editor compatibility
    let htmlText = `
      <table style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; font-size: 14px;">
        <style>
          table { border-collapse: collapse; width: 100%; }
          th { background-color: #f8f9fa; font-weight: bold; text-align: left; }
          th, td { border: 1px solid #dee2e6; padding: 12px 8px; }
          tr:nth-child(even) { background-color: #f8f9fa; }
        </style>
    `;

    let isHeaderProcessed = false;

    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll("th, td");
      const cellTexts = Array.from(cells).map(cell => cell.textContent.trim());
      const isHeaderRow = row.querySelectorAll("th").length > 0 || rowIndex === 0;

      if (isHeaderRow && !isHeaderProcessed) {
        // TSV format - Header row with separator
        tsvText += cellTexts.join("\t") + "\n";
        tsvText += cellTexts.map(() => "---").join("\t") + "\n";

        // Markdown format - Header row
        markdownText += "| " + cellTexts.join(" | ") + " |\n";
        markdownText += "|" + cellTexts.map(() => "---").join("|") + "|\n";

        // HTML format - Header row with enhanced styling
        htmlText += "<thead><tr>";
        cellTexts.forEach(cell => {
          htmlText += `<th style="background-color: #e9ecef; font-weight: bold; padding: 12px 8px; border: 1px solid #dee2e6; text-align: left;">${cell}</th>`;
        });
        htmlText += "</tr></thead><tbody>";
        isHeaderProcessed = true;
      } else {
        // TSV format - Data rows
        tsvText += cellTexts.join("\t") + "\n";

        // Markdown format - Data rows
        markdownText += "| " + cellTexts.join(" | ") + " |\n";

        // HTML format - Data rows with alternating colors
        const bgColor = (rowIndex - (isHeaderProcessed ? 1 : 0)) % 2 === 0 ? "#ffffff" : "#f8f9fa";
        htmlText += `<tr style="background-color: ${bgColor};">`;
        cellTexts.forEach(cell => {
          htmlText += `<td style="padding: 12px 8px; border: 1px solid #dee2e6;">${cell}</td>`;
        });
        htmlText += "</tr>";
      }
    });

    htmlText += "</tbody></table>";

    return {
      tsv: tsvText.trim(),
      markdown: markdownText.trim(),
      html: htmlText
    };
  };

  const handleDownloadCSV = () => {
    if (downloading) return; // Prevent multiple downloads

    try {
      setDownloading(true);
      const { tsv } = extractTableData();

      // Convert TSV to CSV (replace tabs with commas, handle quotes properly)
      const csvContent = tsv
        .split('\n')
        .map(row => {
          // Skip separator rows (containing only dashes)
          if (row.match(/^[\t-]+$/)) return '';

          // Split by tabs and properly escape CSV values
          return row
            .split('\t')
            .map(cell => {
              // If cell contains comma, newline, or quote, wrap in quotes and escape internal quotes
              if (cell.includes(',') || cell.includes('\n') || cell.includes('"')) {
                return `"${cell.replace(/"/g, '""')}"`;
              }
              return cell;
            })
            .join(',');
        })
        .filter(row => row) // Remove empty rows
        .join('\n');

      // Create and download file with a small delay to show spinner
      setTimeout(() => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `table-${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
        setDownloading(false);
      }, 500); // Small delay to ensure spinner is visible

    } catch (err) {
      logger.error('TableWithCopy', 'Download CSV failed', { error: err.message });
      setDownloading(false);
    }
  };

  const handleCopyTable = async () => {
    try {
      const { tsv, html } = extractTableData();
      let copySuccess = false;

      if (navigator.clipboard?.write) {
        // Multi-format copy for maximum compatibility (removed text/markdown as it's not supported)
        const clipboardItem = new ClipboardItem({
          "text/plain": new Blob([tsv], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" })
        });
        await navigator.clipboard.write([clipboardItem]);
        copySuccess = true;
      } else if (navigator.clipboard?.writeText) {
        // Fallback: Copy TSV format (best for spreadsheets)
        await navigator.clipboard.writeText(tsv);
        copySuccess = true;
      } else {
        // Legacy fallback
        const textarea = document.createElement("textarea");
        textarea.value = tsv;
        document.body.appendChild(textarea);
        textarea.select();
        const success = document.execCommand("copy");
        document.body.removeChild(textarea);
        copySuccess = success;
      }

      // Only show checkmark if copy was actually successful
      if (copySuccess) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      logger.error('TableWithCopy', 'Copy table failed', { error: err.message });
      // Don't show checkmark on error
    }
  };

  return (
    <div className="relative my-4 group">
      {/* Table container with border */}
      <div className="photon-table-frame rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 shadow-sm">
        <div className="overflow-x-auto">
          <table ref={tableRef} className="photon-markdown-table w-full">
            {children}
          </table>
        </div>
      </div>

      {/* Action buttons - positioned outside table, bottom-right */}
      <div className="flex justify-end gap-2 mt-2">
        {/* Download CSV button */}
        <CustomTooltip
          content={downloading ? "Downloading CSV..." : "Download as CSV"}
          side="top"
          align="center"
        >
          <button
            type="button"
            aria-label={downloading ? "Downloading CSV" : "Download as CSV"}
            aria-busy={downloading || undefined}
            onClick={handleDownloadCSV}
            className={`photon-icon-button p-2 rounded-full transition-colors duration-200 shadow-lg ${
              downloading
                ? "bg-fill-blue-500 dark:bg-fill-blue-600 opacity-100 cursor-not-allowed"
                : "bg-fill-white dark:bg-fill-gray-800 hover:bg-fill-gray-50 dark:hover:bg-fill-gray-700 border-2 border-edge-gray-300 dark:border-edge-gray-600 opacity-70 group-hover:opacity-100 hover:scale-105"
            }`}
            disabled={downloading}
          >
            {downloading ? (
              <InlineSpinner className="text-ink-on-color" />
            ) : (
              <Download className="w-4 h-4 text-ink-gray-600 dark:text-ink-gray-300" />
            )}
          </button>
        </CustomTooltip>

        {/* Copy button */}
        <CustomTooltip
          content={copied ? "Copied!" : "Copy table"}
          side="top"
          align="center"
        >
          <button
            type="button"
            aria-label={copied ? "Table copied" : "Copy table"}
            onClick={handleCopyTable}
            className={`photon-icon-button ${copied ? 'photon-control-success' : ''} p-2 rounded-full transition-colors duration-200 shadow-lg ${
              copied
                ? "bg-fill-green-500 dark:bg-fill-green-600 opacity-100"
                : "bg-fill-white dark:bg-fill-gray-800 hover:bg-fill-gray-50 dark:hover:bg-fill-gray-700 border-2 border-edge-gray-300 dark:border-edge-gray-600 opacity-70 group-hover:opacity-100 hover:scale-105"
            }`}
            disabled={copied}
          >
            {copied ? (
              <Check className="w-4 h-4 text-ink-on-color animate-pulse" />
            ) : (
              <Copy className="w-4 h-4 text-ink-gray-600 dark:text-ink-gray-300" />
            )}
          </button>
        </CustomTooltip>
      </div>
    </div>
  );
};

export default TableWithCopy;
